<template>
	<EventLayout :event="event" :subtitle="trans('events.certifications.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal(createModalId)">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Modal :id="createModalId">
			<template #title>{{ trans('events.certifications.create') }}</template>
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<LabeledObject required>
					<template #label>{{ trans('events.certifications.participation_type.title') }}</template>
					<select id="select-participation-type" v-model="newAttendancePlan.participation_type">
						<option v-for="type in ParticipationType" :value="type">{{ trans(`events.certifications.participation_type.${type}`) }}</option>
					</select>
				</LabeledObject>
				<LabeledObject full required>
					<template #label>{{ trans('events.certifications.certificate') }}</template>
					<select id="select-certificate" v-model="newAttendancePlan.certificate_id">
						<option :value="0">{{ trans('general.please_select') }}</option>
						<option v-for="certificate in certificates" :key="`certificate-${certificate.id}`" :value="certificate.id">{{ certificate.title }}</option>
					</select>
				</LabeledObject>
				<template v-if="newAttendancePlan.participation_type === ParticipationType.participant">
					<LabeledObject>
						<template #label>{{ trans('events.certifications.course_minimum') }}</template>
						<NumberInput v-model.number="newAttendancePlan.course_minimum" :max="100"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.workshop_minimum') }}</template>
						<NumberInput v-model.number="newAttendancePlan.workshop_minimum" :max="100"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.field_trip_minimum') }}</template>
						<NumberInput v-model.number="newAttendancePlan.field_trip_minimum" :max="100"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.dissertation_minimum') }}</template>
						<NumberInput v-model.number="newAttendancePlan.dissertation_minimum" :max="100"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.percentage') }}</template>
						<NumberInput v-model.number="newAttendancePlan.percentage" :max="100"/>
					</LabeledObject>
				</template>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" :disabled="newAttendancePlan.certificate_id === 0" @click.stop="confirmCreation">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<Modal :id="confirmModalId">
			<template #title>{{ trans('events.certifications.confirm') }}</template>
			<div class="flex flex-col gap-3">
				<p>{{ trans('events.certifications.warning') }}</p>
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
					<LabeledObject>
						<template #label>{{ trans('events.certifications.participation_type.title') }}</template>
						<p>{{ trans(`events.certifications.participation_type.${newAttendancePlan.participation_type}`) }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.certificate') }}</template>
						<p>{{ getCertificateTitleById(newAttendancePlan.certificate_id) }}</p>
					</LabeledObject>
					<template v-if="newAttendancePlan.participation_type === 'participant'">
						<LabeledObject>
							<template #label>{{ trans('events.certifications.course_minimum') }}</template>
							<p>{{ newAttendancePlan.course_minimum }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.certifications.workshop_minimum') }}</template>
							<p>{{ newAttendancePlan.workshop_minimum }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.certifications.field_trip_minimum') }}</template>
							<p>{{ newAttendancePlan.field_trip_minimum }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.certifications.dissertation_minimum') }}</template>
							<p>{{ newAttendancePlan.dissertation_minimum }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.certifications.percentage') }}</template>
							<p>{{ newAttendancePlan.percentage }}%</p>
						</LabeledObject>
					</template>
				</div>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="create">{{ trans('general.confirm') }}</PrimaryButton>
			</template>
		</Modal>
		<Empty v-if="attendancePlans.length === 0"/>
		<CardGrid v-else tight>
			<Card v-for="attendancePlan in attendancePlans" :title="attendancePlan.certificate.title">
				<template #badges>
					<Badge v-if="attendancePlan.participation_type === ParticipationType.participant" :color="Color.Purple" active>{{ trans('events.certifications.participation_type.participant') }}</Badge>
					<Badge v-else :color="Color.Green" active>{{ trans('events.certifications.participation_type.organizer') }}</Badge>
				</template>
				<template v-if="attendancePlan.participation_type === ParticipationType.participant">
					<LabeledObject>
						<template #label>{{ trans('events.certifications.percentage') }}</template>
						<p>{{ attendancePlan.percentage }}%</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.course_minimum') }}</template>
						<p>{{ attendancePlan.course_minimum }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.workshop_minimum') }}</template>
						<p>{{ attendancePlan.workshop_minimum }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.field_trip_minimum') }}</template>
						<p>{{ attendancePlan.field_trip_minimum }}</p>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.certifications.dissertation_minimum') }}</template>
						<p>{{ attendancePlan.dissertation_minimum }}</p>
					</LabeledObject>
				</template>
			</Card>
		</CardGrid>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import {type Certificate} from '@/Types/Certificates/Certificate'
import {ParticipationType} from '@/Types/Enums/ParticipationType'
import {type AttendancePlan} from '@/Types/Events/AttendancePlan'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	event:Event,
	certificates:Array<Certificate>,
	attendancePlans:Array<AttendancePlan>,
}>()

const createModalId:Ref<string> = ref('create-attendance-plan-modal')
const confirmModalId:Ref<string> = ref('confirm-create-attendance-plan-modal')
const working:Ref<boolean> = ref(false)
const event:Ref<Event> = ref(props.event)
const certificates:Ref<Array<Certificate>> = ref(props.certificates)
const attendancePlans:Ref<Array<AttendancePlan>> = ref(props.attendancePlans)
const newAttendancePlan:Ref<AttendancePlan> = ref({
	certificate_id: 0,
	participation_type: ParticipationType.participant,
	percentage: 0,
	course_minimum: 0,
	workshop_minimum: 0,
	field_trip_minimum: 0,
	dissertation_minimum: 0,
} as AttendancePlan)

function confirmCreation():void{
	if(newAttendancePlan.value.certificate_id > 0){
		showModal(confirmModalId.value)
	}
}

function getCertificateTitleById(certificateId:number):string{
	const certificate = certificates.value.find((c:Certificate) => c.id === certificateId)
	if(certificate){
		return certificate.title
	}
	return trans('general.n_a')
}

function create():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.enrollments.certifications.store', event.value.id), newAttendancePlan.value).then(({data}) => {
			showSuccess(trans('general.inserted'))
			attendancePlans.value.unshift(data)
			closeModal(confirmModalId.value)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>